import { useEffect, useRef, useState } from "react";

/* DeferUntilVisible — mounts children only once the placeholder enters
 * (or gets near to) the viewport. Used to keep below-the-fold sections
 * out of the homepage's initial render path so React commits less
 * synchronous work before LCP — a free PageSpeed / TBT win.
 *
 * Strategy:
 *   1. Initially render an empty <div> with the supplied `minHeight` so
 *      the document doesn't re-layout when the real content is mounted.
 *   2. Attach an IntersectionObserver with a generous `rootMargin` so
 *      content mounts well before it actually scrolls into view — the
 *      user never sees a blank section.
 *   3. As a safety net: if the browser lacks IntersectionObserver, or
 *      the user has reduced data preferences, fall back to a single
 *      requestIdleCallback (then setTimeout) so prerendered HTML
 *      crawlers and JS-disabled UAs still see the full content via the
 *      SSR/prerender pipeline (which always renders eagerly).
 *
 * SEO note: the prerender pipeline (puppeteer in scripts/prerender.js)
 * waits for `domcontentloaded` + extra time before snapshotting, so
 * deferred children DO end up in the static HTML emitted to crawlers —
 * confirmed via local prerender run. This keeps Google's First-Pass
 * indexer fed while shaving JS off the user-facing LCP path.
 */
export default function DeferUntilVisible({
  children,
  rootMargin = "400px 0px",
  minHeight = 200,
  testId,
  id,
}) {
  const ref = useRef(null);
  // Prerendered snapshots run inside puppeteer with a Googlebot UA so we
  // detect bot/crawler UAs here and render eagerly — guarantees the
  // deferred content ends up in the static HTML shipped to Google and
  // social crawlers (Slack/Discord/Twitter/Facebook unfurl, GPTBot,
  // ClaudeBot, PerplexityBot all spoof or share these UA fragments).
  const isPrerender =
    typeof navigator !== "undefined" &&
    /prerender|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|whatsapp|gptbot|claudebot|perplexitybot|chatgpt-user|oai-searchbot/i.test(
      navigator.userAgent || ""
    );
  // ALSO mount eagerly when there's a `#hash` in the URL at first paint.
  // Without this, deep-links like `/#faq` and cross-route nav from
  // `/blog → /#faq` land in a state where the target section isn't in
  // the DOM (still a `minHeight` placeholder), so `HashAnchorScroller`
  // can't find `#faq` and the scroll silently no-ops. Acceptable LCP
  // cost: hash-nav users explicitly *want* the deep link, so mounting
  // all sections immediately is the right trade-off for them.
  const isHashNav =
    typeof window !== "undefined" && !!(window.location && window.location.hash);
  const [visible, setVisible] = useState(isPrerender || isHashNav);

  // Listen for an app-wide "expand all deferred" signal. Fired by
  // `HashAnchorScroller` when a `#anchor` click targets an element
  // that hasn't been mounted yet — forces every deferred section to
  // render so the click handler can scroll to the (now-existing) target
  // on its retry tick.
  useEffect(() => {
    if (visible) return;
    const onExpand = () => setVisible(true);
    window.addEventListener("feedpulse:expand-deferred", onExpand);
    return () => window.removeEventListener("feedpulse:expand-deferred", onExpand);
  }, [visible]);

  useEffect(() => {
    if (visible) return;
    if (typeof IntersectionObserver === "undefined") {
      // Old browser: mount after a tiny delay so it doesn't compete with
      // first paint.
      const id = setTimeout(() => setVisible(true), 200);
      return () => clearTimeout(id);
    }
    const node = ref.current;
    if (!node) return;
    // v60.30: The v60.25 requestIdleCallback gate was reverted after
    // production Lighthouse audits showed a 90+ → 80+ Perf regression.
    // The 0-200 ms idle delay compounded across multiple
    // DeferUntilVisible wrappers on the homepage (up to 7 separate
    // idle-wait cycles), adding artificial layout-thrash to LCP. Going
    // back to the original behaviour: mount synchronously the moment
    // the placeholder intersects the viewport.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  // v60.99.73 — when an `id` is provided, render it on the wrapper of
  // BOTH the placeholder AND the visible content. This keeps the anchor
  // target (#faq, #widgets, etc.) in the DOM continuously, so a click
  // on a hash link on first page load lands the user at the correct
  // scroll position immediately instead of waiting for the lazy chunk
  // to download. scrollMarginTop=72 offsets the sticky header.
  const wrapperProps = id
    ? { id, style: { scrollMarginTop: 72 } }
    : {};
  if (visible) {
    return id ? <div {...wrapperProps}>{children}</div> : children;
  }
  return (
    <div
      ref={ref}
      data-testid={testId || "defer-placeholder"}
      style={{ minHeight, ...(id ? { scrollMarginTop: 72 } : {}) }}
      aria-hidden="true"
      {...(id ? { id } : {})}
    />
  );
}
